/**
 * Google Analytics 4 / Google Ads helpers.
 *
 * The gtag() function is installed globally from /index.html. This module
 * exposes a typed/safe wrapper plus a <GAListener/> component that fires
 * `page_view` on every react-router-dom navigation — needed because the
 * SPA does not perform a full page reload.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Internal backend funnel sink — fires in parallel with GA4 so the admin
// dashboard has LIVE counts without depending on GA4's 24-48h pipeline
// or being blocked by ad-blockers.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/** Per-tab session id (UUID-ish, persisted in sessionStorage) used to
 *  group all funnel events fired by the same visitor in the backend. */
function _sessionId() {
  if (typeof window === "undefined") return "ssr";
  try {
    let sid = sessionStorage.getItem("velcor_funnel_sid");
    if (!sid) {
      sid = (crypto.randomUUID ? crypto.randomUUID() :
        `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
      sessionStorage.setItem("velcor_funnel_sid", sid);
    }
    return sid;
  } catch { return "anon"; }
}

/** Fire-and-forget POST to the backend funnel sink. Uses navigator.sendBeacon
 *  when available so the event survives page unload (critical for the
 *  enrollment_complete step which often precedes a redirect). */
function _sendToBackend(event, params) {
  if (!BACKEND_URL) return;
  try {
    const body = JSON.stringify({
      event,
      session_id: _sessionId(),
      city: params.city || null,
      service_type: params.service_type || params.serviceType || null,
      plan_id: params.plan_id || null,
      source: params.source || null,
      value: params.value || null,
      has_zip: params.has_zip === "true" ? true : (params.has_zip === "false" ? false : null),
    });
    const url = `${BACKEND_URL}/api/analytics/funnel`;
    // Beacon: fire-and-forget, no response needed, survives navigation.
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch { /* noop */ }
}

export const GA_MEASUREMENT_ID = "G-R75L1660TY";

// Google Ads conversion IDs — verified live via Tag Assistant on 2026-06-01.
// `AW-…` is your global Ads account; each conversion action has its own
// label appended after `/`. The combined `AW-<id>/<label>` is what Ads
// matches against to count a conversion.
export const GOOGLE_ADS_ID = "AW-18199593933";
export const ADS_CONVERSIONS = {
  // "Enviar formulario de clientes potenciales" (lead/enrollment)
  lead: "AW-18199593933/wxJTCLyEmLccEM2Hn-ZD",
  // Reserved for the "Purchase" conversion when you create it in the
  // Google Ads UI. Until then trackPurchase() falls back to GA4 only.
  purchase: null,
};

/** Safe gtag wrapper — no-op if the global hasn't loaded yet. */
export function gtag(...args) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag(...args);
  } else if (typeof window !== "undefined") {
    // Fallback to dataLayer push so events queued before gtag.js downloads
    // are not lost.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
  }
}

/** Manually dispatch GA4 page_view (SPAs need this). */
export function trackPageView(path) {
  gtag("event", "page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : "",
    page_title: typeof document !== "undefined" ? document.title : "",
  });
}

/**
 * Track a "lead generated" conversion (enroll-complete).
 *
 * Fires TWO gtag events back-to-back:
 *   1. A GA4 `sign_up` event for analytics funnels.
 *   2. A Google Ads `conversion` event with `send_to` pointing at the
 *      "Enviar formulario de clientes potenciales" action — this is what
 *      Tag Assistant looks for. Verified against AW-18199593933/wxJT...ZD.
 */
export function trackLead({ orderNumber, plan, value } = {}) {
  // 1) GA4 lead funnel event
  gtag("event", "sign_up", {
    method: "web_enroll",
    transaction_id: orderNumber,
    plan,
    value: value || 0,
    currency: "USD",
  });
  // 2) Google Ads conversion — the one Tag Assistant validates
  if (ADS_CONVERSIONS.lead) {
    gtag("event", "conversion", {
      send_to: ADS_CONVERSIONS.lead,
      transaction_id: orderNumber || undefined,
      value: value || 0,
      currency: "USD",
    });
  }
}

/** Track a real payment conversion (Stripe checkout completed). */
export function trackPurchase({ invoice, value, currency = "USD" } = {}) {
  gtag("event", "purchase", {
    transaction_id: invoice,
    value: value || 0,
    currency,
  });
}

/**
 * Track when a visitor proceeded through /address WITH a ZIP that was
 * auto-detected by IP geolocation. Lets us measure the conversion lift
 * of the geo-prefill feature in GA4 (compare conversion rate of users
 * who saw the prefill vs those who didn't).
 *
 * @param zip - The auto-detected ZIP code that was used.
 * @param city - Detected city (optional, for segmentation).
 */
export function trackGeoPrefillUsed({ zip, city } = {}) {
  gtag("event", "geo_prefill_used", {
    method: "ip_geolocation",
    detected_zip: zip || "",
    detected_city: city || "",
    // Standard GA4 engagement params so this counts toward funnels
    engagement_time_msec: 1,
  });
}

/**
 * Track when a city landing page (e.g. /electricity-arlington) is viewed.
 * Lets us segment GA4 by city to see which campaigns are bringing traffic.
 */
export function trackCityLandingView({ city, slug } = {}) {
  gtag("event", "city_landing_view", {
    city: city || "",
    city_slug: slug || "",
  });
}

/**
 * Track when a visitor clicked a city landing CTA ("See my price" or
 * "Verify my address"). Most important conversion signal — pairs with
 * `city_landing_view` to compute click-through rate per city. The
 * `utm_campaign={slug}` in the destination URL already gives GA4 the
 * acquisition attribution; this event adds explicit engagement timing.
 */
export function trackCityLandingCtaClick({ city, slug, ctaPosition } = {}) {
  gtag("event", "city_landing_cta_click", {
    city: city || "",
    city_slug: slug || "",
    cta_position: ctaPosition || "unknown", // "top" | "bottom"
  });
}

/**
 * <GAListener/> — drop-in component mounted once at the app root. Watches
 * react-router-dom location and dispatches page_view on every change.
 */
export default function GAListener() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

// ──────────────────────────────────────────────────────────────────────
// Funnel events (Feb 2026) — 5-step conversion pipeline.
//
// These map 1-to-1 to the steps in the Velcor enrollment funnel and let
// us identify exactly where visitors abandon:
//
//   1. address_search      → user typed an address (any entry point)
//   2. view_plans          → user landed on /residential|/apartments|/commercial
//   3. plan_selected       → user clicked a specific plan card
//   4. enrollment_start    → user landed on /enroll (filling form)
//   5. enrollment_complete → user submitted /enroll successfully (lead)
//
// To analyze in GA4:
//   • Reports → Engagement → Events: each event shows total fires
//   • Explore → Funnel Exploration: chain these 5 events in order to
//     visualize step-by-step drop-off rates per segment (device, city,
//     utm_campaign, etc.)
//   • Realtime → see events trickle in during ad campaigns
//
// All events include `engagement_time_msec: 1` so they count toward the
// session engagement (GA4 silently drops events with 0 engagement that
// aren't pre-registered as "key events"). The `source` param identifies
// which entry point triggered the event (home hero vs city landing vs
// quickpay flow, etc.) so we can compare funnel performance by source.
// ──────────────────────────────────────────────────────────────────────

/**
 * Step 1/5 — A visitor typed an address into ANY search bar (home hero,
 * /address page, city landing top or bottom CTA, ESIID form).
 *
 * @param source - "home_hero" | "city_hero" | "city_bottom" | "address_page" | "esiid"
 * @param hasZip - true if a ZIP was already known when the search fired
 * @param city  - optional city slug or name (for city-landing entries)
 */
export function trackAddressSearch({ source = "unknown", hasZip = false, city = "" } = {}) {
  gtag("event", "address_search", {
    source,
    has_zip: hasZip ? "true" : "false",
    city: city || "",
    engagement_time_msec: 1,
  });
  _sendToBackend("address_search", { source, has_zip: hasZip ? "true" : "false", city });
}

/**
 * Step 2/5 — Visitor reached the plan picker page (/residential, /apartments,
 * /commercial). Fires once per page load via PlanPage.
 *
 * @param serviceType - "residential" | "apartments" | "commercial"
 * @param zip         - ZIP that drove plan selection (for city segmentation)
 * @param source      - how they got here ("home_hero" | "city_landing" | "address" | "direct")
 */
export function trackViewPlans({ serviceType = "", zip = "", source = "unknown" } = {}) {
  gtag("event", "view_plans", {
    service_type: serviceType,
    zip: zip || "",
    source,
    engagement_time_msec: 1,
  });
  _sendToBackend("view_plans", { service_type: serviceType, source });
}

/**
 * Step 3/5 — Visitor clicked a specific plan card ("Choose this plan").
 * Includes plan economics so GA4 can attribute revenue to upstream sources.
 *
 * @param planId     - canonical plan id (e.g. "velcor-eco-12")
 * @param planName   - display name (e.g. "Velcor Eco 12")
 * @param termMonths - 12 | 24 | 36
 * @param rateCents  - displayed kWh rate × 100 (avoids floating-point in GA)
 * @param avgBill    - estimated monthly bill in USD (for funnel value)
 */
export function trackPlanSelected({ planId = "", planName = "", termMonths = 0, rateCents = 0, avgBill = 0 } = {}) {
  gtag("event", "plan_selected", {
    plan_id: planId,
    plan_name: planName,
    term_months: termMonths,
    rate_cents: rateCents,
    value: avgBill || 0,
    currency: "USD",
    engagement_time_msec: 1,
  });
  _sendToBackend("plan_selected", { plan_id: planId, value: avgBill });
}

/**
 * Step 4/5 — Visitor landed on /enroll (form rendered, ready to fill).
 * Fires once per /enroll mount. We DON'T track keystrokes — only the
 * intent signal (page reached).
 *
 * @param planId      - canonical plan id selected in step 3
 * @param serviceType - residential|apartments|commercial
 */
export function trackEnrollmentStart({ planId = "", serviceType = "" } = {}) {
  gtag("event", "enrollment_start", {
    plan_id: planId,
    service_type: serviceType,
    engagement_time_msec: 1,
  });
  _sendToBackend("enrollment_start", { plan_id: planId, service_type: serviceType });
}

/**
 * Step 5/5 — Lead successfully submitted (POST /api/leads returned 200).
 * This is the SAME conversion signal as `trackLead` BUT framed for the
 * funnel exploration (no Google Ads side-effect — keep that separate via
 * trackLead so the Ads conversion isn't double-counted).
 *
 * @param orderNumber - VEL-XXXXXX returned by the backend
 * @param planId      - selected plan id
 * @param depositUsd  - deposit amount if any (0 for low-risk leads)
 * @param value       - first-month estimated bill (for funnel monetization)
 */
export function trackEnrollmentComplete({ orderNumber = "", planId = "", depositUsd = 0, value = 0 } = {}) {
  gtag("event", "enrollment_complete", {
    transaction_id: orderNumber,
    plan_id: planId,
    deposit_usd: depositUsd || 0,
    value: value || 0,
    currency: "USD",
    engagement_time_msec: 1,
  });
  _sendToBackend("enrollment_complete", { plan_id: planId, value });
}
